import type { ContentType } from '@/interfaces';
import clientStorage from '@/services/ClientStorage';
import { OFFLINE_STORAGE_NAMESPACE } from './constants';
import {
    cleanUpItemsForUserFromDb,
    clearAllItemsFromDb,
    deleteExpiredItemsFromDb,
    deleteItemFromDb,
    getAllItemsForUser,
    getItemFromDb,
    getReadableItemByPathFromDb,
    refreshItemExpiryInDb,
    saveItemToDb,
} from './db';
import { supportsServiceWorker } from './serviceWorker';
import type { OfflineDownloadArgs, OfflineItem, OfflineItemMeta } from './types';

export type { OfflineItem, OfflineItemMeta } from './types';

/** True when the browser supports the storage APIs the offline reader needs. */
export function canUseOfflineReports(): boolean {
    return typeof window !== 'undefined' && 'indexedDB' in window;
}

/** True when offline reading can work end to end: storage for the PDFs and a service worker to serve the reader offline. */
export function canUseOfflineMode(): boolean {
    return canUseOfflineReports() && supportsServiceWorker();
}

/**
 * Fetches a PDF while online and stores it (bytes + metadata)
 * in IndexedDB under the given user.
 */
export async function downloadItemForOfflineReading(
    userId: number,
    { contentType, itemId, title, viewerUrl, pdfUrl, watermarkText, summary, displayDate }: OfflineDownloadArgs
): Promise<void> {
    if (!canUseOfflineReports()) {
        throw new Error('Offline reading is not supported in this browser.');
    }
    if (await getItemFromDb(userId, contentType, itemId)) return;

    const response = await fetch(pdfUrl);
    if (!response.ok) {
        throw new Error(`Failed to download PDF (${response.status}).`);
    }
    const pdf = await response.blob();

    await saveItemToDb(userId, {
        itemId,
        contentType,
        title,
        viewerUrl,
        validatedAt: new Date().toISOString(),
        pdf,
        watermarkText,
        summary,
        displayDate,
    });
}

export async function refreshOfflineItemExpiry(
    userId: number,
    contentType: ContentType,
    itemId: string
): Promise<void> {
    if (!canUseOfflineReports()) return;
    return refreshItemExpiryInDb(userId, contentType, itemId);
}

/**
 * Returns the given user's downloaded items, newest first, without the `pdf` field. Items past retention are left out,
 * so the list doesn't depend on whether the cleanup has already deleted them.
 */
export async function listOfflineItems(userId: number): Promise<OfflineItemMeta[]> {
    const items = await getAllItemsForUser(userId);
    return items.map(({ pdf: _pdf, ...meta }: OfflineItem) => meta);
}

/**
 * Returns the downloaded report that a page path points to (with or without query parameters), without the `pdf`
 * field, or null when the path isn't a report the user can read: not downloaded, expired, or evicted.
 */
export async function getReadableOfflineItem(userId: number, path: string): Promise<OfflineItemMeta | null> {
    const item = await getReadableItemByPathFromDb(userId, path);
    if (!item) return null;
    const { pdf: _pdf, ...meta } = item;
    return meta;
}

/**
 * Returns a short-lived blob: URL for a downloaded PDF, or null if not found.
 * The caller MUST call URL.revokeObjectURL() when done.
 */
export async function getOfflineItemPdfUrl(
    userId: number,
    contentType: ContentType,
    itemId: string
): Promise<string | null> {
    const item = await getItemFromDb(userId, contentType, itemId);
    if (!item?.pdf) return null;
    return URL.createObjectURL(item.pdf);
}

export async function deleteOfflineItem(userId: number, contentType: ContentType, itemId: string): Promise<void> {
    await deleteItemFromDb(userId, contentType, itemId);
}

/** Removes every locally stored offline related data. */
export async function clearAllOfflineData(): Promise<void> {
    clientStorage.clearNamespace(OFFLINE_STORAGE_NAMESPACE);
    await clearAllItemsFromDb();
}

/** Deletes other users' items and the user's items past retention, and drops the PDF of the user's expired items. */
export async function cleanUpOfflineItems(userId: number): Promise<void> {
    if (!canUseOfflineReports()) return;
    await cleanUpItemsForUserFromDb(userId);
}

export async function deleteExpiredOfflineItems(): Promise<void> {
    if (!canUseOfflineReports()) return;
    await deleteExpiredItemsFromDb();
}
